/**
 * The Publish sheet's controller (plan 3.5, 7.6), shared by the web dialog
 * (`PublishSheet.web.tsx`) and the native form sheet (`PublishSheet.tsx`):
 * the reducer of `publish-sheet-model.ts`, the address field and its
 * availability check, the switches, and every call (publish, update, rename,
 * roll back, the search switch, unpublish). Rendering stays in the twins.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PublishedSiteDetail } from '@tabbio/types';
import { slugifySiteName } from '@tabbio/utils/site-names';
import { useToast } from '@/hooks/useToast';
import {
  useArtifactHeadChecks,
  useInvalidateSites,
  usePublishingCapability,
  useSiteDeploys,
  useSiteNameCheck,
  useSiteSocketNudge,
} from '@/hooks/useSites';
import { confirmDestructive } from '@/lib/confirm-destructive';
import { trackProductEvent } from '@/lib/product-analytics';
import { QUERY_KEYS } from '@/lib/query-keys';
import {
  getArtifactSite,
  getSiteDeploys,
  publishArtifactSite,
  redeploySite,
  rollbackSite,
  unpublishSite,
  updateSite,
} from '@/lib/sites-api';
import { useSubscriptionState } from '@/providers/useSubscriptionState';
import {
  initialPublishSheetState,
  initialSiteName,
  isPublishSheetBusy,
  manageModel,
  nameStatusModel,
  publishErrorFrom,
  publishFormModel,
  publishSheetReducer,
  type PublishSheetAction,
  type PublishSheetIntent,
} from './publish-sheet-model';
import { sanitizeSiteNameInput } from './site-name-check';
import { canShareSiteLink, copySiteLink, openSite, shareSiteLink } from './site-share';

/** While a deploy builds the sheet also polls, in case the socket nudge is missed. */
const BUILDING_POLL_MS = 3_000;

export type PublishSheetOptions = {
  artifactId: string;
  intent: PublishSheetIntent;
  /** The `publishArtifact` card's suggestion, used when the artifact has no address yet. */
  suggestedName?: string | null;
  /** The artifact's title: a slug of it seeds the address when nothing else does. */
  artifactTitle?: string | null;
};

export function usePublishSheet({ artifactId, intent, suggestedName, artifactTitle }: PublishSheetOptions) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const queryClient = useQueryClient();
  const invalidateSites = useInvalidateSites();
  const capability = usePublishingCapability();
  const { snapshot } = useSubscriptionState();
  const tier = snapshot?.tier ?? null;
  const [state, dispatch] = useReducer(publishSheetReducer, intent, initialPublishSheetState);
  const [name, setNameRaw] = useState('');
  const [renameValue, setRenameRaw] = useState('');
  const [indexable, setIndexable] = useState(false);
  const [showBadge, setShowBadge] = useState(true);

  // The artifact's site, read fresh each time the sheet opens.
  const siteQuery = useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_SITE(artifactId),
    queryFn: () => getArtifactSite(artifactId),
    enabled: Boolean(artifactId),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
    refetchInterval: state.view === 'building' ? BUILDING_POLL_MS : false,
  });

  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    trackProductEvent('publish_sheet_opened', { intent });
  }, [intent]);

  // First answer: where the sheet opens, and the field's starting values.
  useEffect(() => {
    if (state.view !== 'loading') return;
    if (siteQuery.isFetching && !siteQuery.isFetchedAfterMount) return;
    if (siteQuery.isError) {
      dispatch({ type: 'loaded', site: null });
      return;
    }
    if (!siteQuery.isFetchedAfterMount) return;
    const site = siteQuery.data ?? null;
    dispatch({ type: 'loaded', site });
    const seeded =
      initialSiteName({ site, suggestedName }) ||
      (artifactTitle ? slugifySiteName(artifactTitle) : '');
    setNameRaw(seeded);
    setRenameRaw(site?.name ?? '');
    setIndexable(site?.indexable ?? false);
    setShowBadge(site?.showBadge ?? true);
  }, [
    artifactTitle,
    siteQuery.data,
    siteQuery.isError,
    siteQuery.isFetchedAfterMount,
    siteQuery.isFetching,
    state.view,
    suggestedName,
  ]);

  // Fresher copies (the nudge, the poll, another screen's change).
  const lastSite = useRef(siteQuery.data);
  useEffect(() => {
    if (state.view === 'loading') return;
    if (siteQuery.data === lastSite.current) return;
    lastSite.current = siteQuery.data;
    if (siteQuery.data !== undefined) dispatch({ type: 'refreshed', site: siteQuery.data });
  }, [siteQuery.data, state.view]);

  // `site:update` for this artifact refreshes it at once.
  const refetchSite = siteQuery.refetch;
  useSiteSocketNudge(Boolean(artifactId), (event) => {
    if (event.artifactId === artifactId) void refetchSite();
  });

  // A settled build (either way) refreshes the list, the cards and the plan count.
  const previousView = useRef(state.view);
  useEffect(() => {
    const was = previousView.current;
    previousView.current = state.view;
    if (was === 'building' && state.view !== 'building') {
      invalidateSites();
      if (state.view === 'live') trackProductEvent('publish_sheet_completed', { outcome: 'published' });
    }
  }, [invalidateSites, state.view]);

  // A deploy that fails after the call's 10 s wait never becomes the site's
  // current deploy, so the build is also watched in the site history.
  const building = state.view === 'building' && Boolean(state.site?.id) && Boolean(state.pendingDeployId);
  const buildWatch = useQuery({
    queryKey: [...QUERY_KEYS.SITE_DEPLOYS(state.site?.id ?? ''), 'building'],
    queryFn: () => getSiteDeploys(state.site?.id as string),
    enabled: building,
    refetchInterval: building ? BUILDING_POLL_MS : false,
    retry: false,
  });
  useEffect(() => {
    if (!building || !buildWatch.data) return;
    const pending = buildWatch.data.deploys.find((deploy) => deploy.id === state.pendingDeployId);
    if (pending?.status === 'failed') {
      dispatch({ type: 'failed', error: { kind: 'failed', message: '' } });
      invalidateSites();
    }
  }, [buildWatch.data, building, invalidateSites, state.pendingDeployId]);

  const headChecks = useArtifactHeadChecks(artifactId, state.view === 'form');
  const nameCheck = useSiteNameCheck({
    name,
    enabled: state.view === 'form' || state.view === 'publishing',
    artifactId,
    errorMessage: t('chat.artifacts.publish.checkFailed'),
  });
  const renameCheck = useSiteNameCheck({
    name: renameValue,
    enabled: state.view === 'rename' || state.view === 'renaming',
    artifactId,
    currentName: state.site?.name ?? null,
    errorMessage: t('chat.artifacts.publish.checkFailed'),
  });
  const deploys = useSiteDeploys(state.site?.id, state.view === 'history');

  const form = publishFormModel({
    state,
    check: nameCheck,
    capability,
    tier,
    headErrors: headChecks.data ?? 0,
  });
  const renameStatus = nameStatusModel(renameCheck, state.error?.kind === 'name' ? state.error : null);
  const canRename =
    state.view === 'rename' && renameCheck.status === 'available' && state.error?.kind !== 'name';
  const manage = useMemo(() => (state.site && state.site.url ? manageModel(state.site) : null), [state.site]);

  const setName = useCallback((value: string) => {
    setNameRaw(sanitizeSiteNameInput(value));
    dispatch({ type: 'dismiss-error' });
  }, []);
  const setRenameValue = useCallback((value: string) => {
    setRenameRaw(sanitizeSiteNameInput(value));
    dispatch({ type: 'dismiss-error' });
  }, []);

  const afterSuccess = useCallback(
    (site: PublishedSiteDetail) => {
      queryClient.setQueryData(QUERY_KEYS.ARTIFACT_SITE(artifactId), site);
      invalidateSites();
    },
    [artifactId, invalidateSites, queryClient],
  );

  const run = useCallback(
    async (action: PublishSheetAction, call: () => Promise<PublishedSiteDetail>) => {
      dispatch({ type: 'submit', action });
      try {
        const site = await call();
        lastSite.current = site;
        dispatch({ type: 'succeeded', site });
        afterSuccess(site);
        if (action === 'publish' && site.currentDeploy?.status !== 'building') {
          trackProductEvent('publish_sheet_completed', { outcome: 'published' });
        }
        return site;
      } catch (error) {
        dispatch({ type: 'failed', error: publishErrorFrom(error) });
        if (action === 'publish') trackProductEvent('publish_sheet_completed', { outcome: 'refused' });
        return null;
      }
    },
    [afterSuccess],
  );

  const publish = useCallback(() => {
    if (!form.canPublish) return;
    void run('publish', () =>
      publishArtifactSite(artifactId, {
        name,
        indexable,
        ...(capability?.canHideBadge ? { showBadge } : {}),
      }),
    );
  }, [artifactId, capability?.canHideBadge, form.canPublish, indexable, name, run, showBadge]);

  const update = useCallback(() => {
    const siteId = state.site?.id;
    if (!siteId) return;
    void run('update', () => redeploySite(siteId));
  }, [run, state.site?.id]);

  const rename = useCallback(() => {
    const siteId = state.site?.id;
    if (!siteId || !canRename) return;
    void run('rename', () => updateSite(siteId, { name: renameValue })).then((site) => {
      if (site) toast.success(t('chat.artifacts.publish.renamed'));
    });
  }, [canRename, renameValue, run, state.site?.id, t, toast]);

  const rollback = useCallback(
    (deployId: string) => {
      const siteId = state.site?.id;
      if (!siteId) return;
      void run('rollback', () => rollbackSite(siteId, deployId)).then((site) => {
        if (site) {
          toast.success(t('chat.artifacts.publish.rolledBack'));
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SITE_DEPLOYS(siteId) });
        }
      });
    },
    [queryClient, run, state.site?.id, t, toast],
  );

  /** The search switch: part of the form before publishing, a rebuild after. */
  const changeIndexable = useCallback(
    (next: boolean) => {
      setIndexable(next);
      const siteId = state.site?.id;
      if (state.view === 'manage' && siteId) {
        void run('settings', () => updateSite(siteId, { indexable: next })).then((site) => {
          if (!site) setIndexable(!next);
        });
      }
    },
    [run, state.site?.id, state.view],
  );

  const unpublish = useCallback(async () => {
    const siteId = state.site?.id;
    if (!siteId || isPublishSheetBusy(state)) return;
    const confirmed = await confirmDestructive({
      title: t('chat.artifacts.publish.unpublishTitle'),
      message: t('chat.artifacts.publish.unpublishBody'),
      confirmLabel: t('chat.artifacts.publish.unpublish'),
      cancelLabel: t('actions.cancel'),
    });
    if (!confirmed) return;
    dispatch({ type: 'unpublish' });
    try {
      const site = await unpublishSite(siteId);
      lastSite.current = site;
      dispatch({ type: 'unpublished', site });
      afterSuccess(site);
      toast.success(t('chat.artifacts.publish.unpublished'));
    } catch (error) {
      dispatch({ type: 'failed', error: publishErrorFrom(error) });
      dispatch({ type: 'go', view: 'manage' });
      toast.error(t('chat.artifacts.publish.unpublishFailed'));
    }
  }, [afterSuccess, state, t, toast]);

  const url = state.site?.url ?? '';
  const copyLink = useCallback(async () => {
    if (!url) return;
    if (await copySiteLink(url)) toast.success(t('chat.artifacts.actions.linkCopied'));
    else toast.error(t('chat.artifacts.actions.copyFailed'));
  }, [t, toast, url]);
  const share = useCallback(async () => {
    if (!url) return;
    const outcome = await shareSiteLink(url, state.site?.title ?? '');
    if (outcome === 'copied') toast.success(t('chat.artifacts.actions.linkCopied'));
    else if (outcome === 'failed') toast.error(t('chat.artifacts.actions.copyFailed'));
  }, [state.site?.title, t, toast, url]);
  const open = useCallback(() => {
    if (url) void openSite(url);
  }, [url]);

  return {
    state,
    dispatch,
    capability,
    tier,
    name,
    setName,
    renameValue,
    setRenameValue,
    indexable,
    changeIndexable,
    showBadge,
    setShowBadge,
    nameCheck,
    renameCheck,
    renameStatus,
    canRename,
    form,
    manage,
    deploys,
    canShare: canShareSiteLink(),
    publish,
    update,
    rename,
    rollback,
    unpublish,
    copyLink,
    share,
    open,
    retryLoad: () => void siteQuery.refetch(),
  };
}

export type PublishSheetController = ReturnType<typeof usePublishSheet>;
